import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, MessageSquare, PiggyBank, Sparkles, Loader2, Wallet, ChevronRight } from "lucide-react";
import AppShell from "../components/AppShell";
import { EmptyData, Spinner } from "../components/Shared";
import UpgradeDialog from "../components/UpgradeDialog";
import { api, gbp, signedGbp, catColor } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

export default function Dashboard() {
    const { isPro } = useAuth();
    const navigate = useNavigate();
    const [month, setMonth] = useState(null);
    const [data, setData] = useState(null);
    const [insight, setInsight] = useState(null);
    const [upgrade, setUpgrade] = useState(false);
    const [safe, setSafe] = useState(null);

    useEffect(() => { api.get("/budget").then((r) => setSafe(r.data)).catch(() => {}); }, []);

    useEffect(() => {
        setInsight(null);
        api.get("/overview", { params: month ? { month } : {} }).then((r) => {
            setData(r.data);
            if (!r.data.empty) api.get("/insights", { params: { month: r.data.month.month } }).then((i) => setInsight(i.data)).catch(() => setInsight({ headline: "Here's where your money went.", explanation: "", nudge: "" }));
        });
    }, [month]);

    if (!data) return <AppShell title="Overview" subtitle="Where has my money gone?" testid="dashboard-page"><Spinner /></AppShell>;
    if (data.empty) return <AppShell title="Overview" subtitle="Where has my money gone?" testid="dashboard-page"><EmptyData onLoaded={() => setMonth((m) => (m ? null : ""))} /></AppShell>;

    const m = data.month;
    const maxCat = m.categories[0]?.amount || 1;
    const up = (data.diff || 0) > 0;

    return (
        <AppShell title={m.label} subtitle="Where has my money gone?" testid="dashboard-page"
            actions={<MonthPicker months={data.months} value={m.month} onChange={setMonth} />}>
            <UpgradeDialog open={upgrade} onOpenChange={setUpgrade} feature="Savings Finder" />

            {/* Headline insight */}
            <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-primary/30 bg-gradient-to-br from-secondary via-card to-card p-6 sm:p-8" data-testid="insight-card">
                <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-primary"><Sparkles className="h-3.5 w-3.5" /> Sift says</p>
                {insight ? (
                    <>
                        <h2 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground sm:text-2xl" data-testid="insight-headline">{insight.headline}</h2>
                        {insight.explanation && <p className="mt-3 max-w-3xl text-base leading-relaxed text-foreground/85" data-testid="insight-explanation">{insight.explanation}</p>}
                        {insight.nudge && (
                            <div className="mt-5 flex flex-wrap items-center gap-3">
                                <p className="text-sm font-semibold text-foreground" data-testid="insight-nudge">{insight.nudge}</p>
                                <Button size="sm" onClick={() => (isPro ? navigate("/savings") : setUpgrade(true))} data-testid="insight-savings-btn" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                                    <PiggyBank className="mr-1.5 h-4 w-4" /> Yes, find savings
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => navigate("/chat")} data-testid="insight-ask-btn" className="text-muted-foreground hover:text-foreground">
                                    <MessageSquare className="mr-1.5 h-4 w-4" /> Ask something else
                                </Button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="insight-loading"><Loader2 className="h-4 w-4 animate-spin" /> Reading your month…</div>
                )}
            </motion.section>

            {/* Money hero stats */}
            <section className="mt-6 grid gap-4 sm:grid-cols-4" data-testid="stats-row">
                <Stat label="Spent this month" value={gbp(m.spend, 0)} testid="stat-spend" />
                <Stat label={data.baseline_spend != null ? "vs your usual" : "Money in"}
                    value={data.baseline_spend != null ? signedGbp(data.diff) : gbp(m.income, 0)}
                    tone={data.baseline_spend != null ? (up ? "bad" : "good") : "neutral"}
                    sub={data.baseline_spend != null ? `usual ${gbp(data.baseline_spend, 0)}` : undefined} testid="stat-diff" />
                <Stat label="Left over" value={signedGbp(m.income - m.spend)} tone={m.income - m.spend >= 0 ? "good" : "bad"} sub={`in ${gbp(m.income, 0)}`} testid="stat-leftover" />
                <button onClick={() => navigate("/budget")} data-testid="stat-safe" className="group rounded-2xl border border-primary/30 bg-primary/10 p-5 text-left transition-colors hover:bg-primary/15">
                    <p className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-primary"><Wallet className="h-3 w-3" /> Safe to spend / day</p>
                    {safe?.saved && safe.safe ? (
                        <><p className={`mt-1 font-heading text-3xl font-black tracking-tight ${safe.safe.over_budget ? "text-red-400" : "text-emerald-400"}`}>{gbp(safe.safe.safe_daily, 0)}</p>
                            <p className="mt-0.5 text-xs text-muted-foreground">{safe.safe.days_left} days to payday</p></>
                    ) : (
                        <><p className="mt-1 font-heading text-lg font-bold text-foreground">Set up payday</p>
                            <p className="mt-0.5 flex items-center text-xs text-muted-foreground">Sift has pre-filled it <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" /></p></>
                    )}
                </button>
            </section>

            <div className="mt-6 grid gap-6 lg:grid-cols-5">
                {/* Category bars */}
                <section className="rounded-2xl border border-border bg-card p-6 lg:col-span-3" data-testid="category-breakdown">
                    <h3 className="font-heading text-base font-bold text-foreground">Where it went</h3>
                    <ul className="mt-4 space-y-3">
                        {m.categories.slice(0, 10).map((c, i) => (
                            <li key={c.category} className="group cursor-pointer" onClick={() => navigate(`/transactions?month=${m.month}&category=${encodeURIComponent(c.category)}`)} data-testid={`category-row-${i}`}>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="flex items-center gap-2 text-foreground/90 group-hover:text-foreground"><span className="h-2.5 w-2.5 rounded-full" style={{ background: catColor(c.category) }} />{c.category}</span>
                                    <span className="font-mono font-semibold text-foreground">{gbp(c.amount, 0)}</span>
                                </div>
                                <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-secondary">
                                    <motion.div initial={{ width: 0 }} animate={{ width: `${(c.amount / maxCat) * 100}%` }} transition={{ duration: 0.6, delay: i * 0.04 }} className="h-full rounded-full" style={{ background: catColor(c.category) }} />
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* Changes + merchants */}
                <div className="space-y-6 lg:col-span-2">
                    {data.top_changes?.length > 0 && (
                        <section className="rounded-2xl border border-border bg-card p-6" data-testid="top-changes">
                            <h3 className="font-heading text-base font-bold text-foreground">What changed</h3>
                            <ul className="mt-3 space-y-2.5">
                                {data.top_changes.map((d) => (
                                    <li key={d.category} className="flex items-center justify-between text-sm">
                                        <span className="text-foreground/85">{d.category}</span>
                                        <span className={`flex items-center gap-1 font-mono font-semibold ${d.diff > 0 ? "text-red-400" : "text-emerald-400"}`}>
                                            {d.diff > 0 ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}{signedGbp(d.diff)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                    <section className="rounded-2xl border border-border bg-card p-6" data-testid="top-merchants">
                        <h3 className="font-heading text-base font-bold text-foreground">Top places</h3>
                        <ul className="mt-3 space-y-2.5">
                            {m.top_merchants.slice(0, 6).map((x) => (
                                <li key={x.merchant} className="flex items-center justify-between text-sm">
                                    <span className="truncate text-foreground/85">{x.merchant}</span>
                                    <span className="font-mono font-semibold text-foreground">{gbp(x.amount, 0)}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                </div>
            </div>
        </AppShell>
    );
}

function Stat({ label, value, sub, tone = "neutral", testid }) {
    const color = tone === "good" ? "text-emerald-400" : tone === "bad" ? "text-red-400" : "text-foreground";
    return (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className={`mt-1 font-heading text-3xl font-black tracking-tight ${color}`}>{value}</p>
            {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
        </div>
    );
}

export function MonthPicker({ months, value, onChange }) {
    return (
        <div className="flex flex-wrap gap-1.5" data-testid="month-picker">
            {months.map((mm) => (
                <button key={mm.month} onClick={() => onChange(mm.month)} data-testid={`month-${mm.month}`}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${mm.month === value ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                    {mm.label.split(" ")[0].slice(0, 3)} {mm.label.split(" ")[1].slice(2)}
                </button>
            ))}
        </div>
    );
}
